import { useState, useMemo } from "react";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceArea,
  Tooltip,
} from "recharts";
import type { WeeklyProgressVM } from "./types";
import { categoryStrokeAlpha } from "./categoryColors";

interface Props {
  vm: WeeklyProgressVM;
}

export function DaysTab({ vm }: Props) {
  const { days, categoryOrder, categoryColors } = vm;
  const [focusedCat, setFocusedCat] = useState<string | null>(null);

  const chartConfig = useMemo<ChartConfig>(() => {
    const config: ChartConfig = {};
    for (const cat of categoryOrder) {
      config[cat] = { label: cat, color: categoryColors[cat].stroke };
    }
    return config;
  }, [categoryOrder, categoryColors]);

  const toggleFocus = (cat: string) => {
    setFocusedCat((prev) => (prev === cat ? null : cat));
  };

  return (
    <div className="space-y-2">
      <ChartContainer config={chartConfig} className="h-44 w-full">
        <ComposedChart data={days} barCategoryGap="18%">
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
          <YAxis yAxisId="tasks" hide allowDecimals={false} domain={[0, "auto"]} />
          <YAxis yAxisId="points" hide orientation="right" domain={[0, "auto"]} />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0]?.payload;
              const cats = categoryOrder
                .filter((c) => row?.[c] > 0)
                .sort((a, b) => (row[b] ?? 0) - (row[a] ?? 0));
              return (
                <div className="rounded-lg border bg-background px-3 py-2 text-xs shadow-xl">
                  <p className="font-medium mb-1">
                    {label}
                    {row?.isToday && (
                      <span className="text-muted-foreground ml-1">(foreløpig)</span>
                    )}
                  </p>
                  {cats.map((c) => (
                    <div key={c} className="flex items-center gap-1.5">
                      <span
                        className="h-2 w-2 rounded-full inline-block"
                        style={{ backgroundColor: categoryColors[c].stroke }}
                      />
                      <span className="text-muted-foreground">{c}</span>
                      <span className="ml-auto font-medium tabular-nums">{row[c]}</span>
                    </div>
                  ))}
                  <div className="border-t mt-1 pt-1 text-muted-foreground flex justify-between">
                    <span>Totalt</span>
                    <span className="font-medium">{row?._totalTasks}</span>
                  </div>
                  {row?._pointsMA != null && (
                    <div className="text-muted-foreground flex justify-between">
                      <span>Poengtrend</span>
                      <span className="font-medium">{row._pointsMA}</span>
                    </div>
                  )}
                </div>
              );
            }}
          />
          {categoryOrder.map((cat) => (
            <Bar
              key={cat}
              yAxisId="tasks"
              dataKey={cat}
              stackId="tasks"
              fill={categoryColors[cat].stroke}
              fillOpacity={
                focusedCat === null ? 0.85 : focusedCat === cat ? 1 : 0.15
              }
              radius={[2, 2, 0, 0]}
            />
          ))}
          {days.length > 0 && days[days.length - 1].isToday && (
            <ReferenceArea
              yAxisId="tasks"
              x1={days[days.length - 1].label}
              x2={days[days.length - 1].label}
              fill="hsl(var(--background))"
              fillOpacity={0.55}
              strokeDasharray="3 3"
              stroke="hsl(var(--muted-foreground))"
              strokeOpacity={0.4}
            />
          )}
          <Line
            yAxisId="points"
            type="monotone"
            dataKey="_pointsMA"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            strokeDasharray="4 3"
            dot={false}
            name="Poengtrend"
          />
        </ComposedChart>
      </ChartContainer>

      {/* Legend with totals & focus */}
      {categoryOrder.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 px-2">
          {categoryOrder.map((cat) => {
            const total = vm.categoryTotals.find((c) => c.category === cat)?.total ?? 0;
            const dimmed = focusedCat !== null && focusedCat !== cat;
            return (
              <button
                key={cat}
                onClick={() => toggleFocus(cat)}
                className="flex items-center gap-1 text-[10px] text-muted-foreground transition-opacity"
                style={{ opacity: dimmed ? 0.35 : 1 }}
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: categoryColors[cat].stroke }}
                />
                {cat} ({total})
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
